import clickUrl from '../../assets/audio/sfx/button_click.mp3';
import { mixer } from '../../audio/audio-mixer.js';
import { between, stepParticles, type Particle } from './particle-field.js';

/** Anything pressable gives off sparks and a puff of dust, except the door's gem, which has its own show. */
const PRESSABLE = 'button:not(:disabled),summary, label.creator-choice:not(.locked), .creator-segmented label, label.party-choice:not(:has(input:disabled))';
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Makes buttons feel like stone and metal: each click sounds, and throws sparks and a little dust from behind the button
 * itself, not from the pointer. One overlay canvas serves the whole page and only runs
 * while something is in the air.
 */
export function mountClickParticles() {
  const canvas = document.createElement('canvas'), context = canvas.getContext('2d')!;
  canvas.className = 'click-particles'; canvas.setAttribute('aria-hidden', 'true');
  document.body.append(canvas);
  /** Each click is its own puff, remembering the button it came from so the particles can be hidden behind it. */
  let bursts: { particles: Particle[]; button: { left: number; top: number; width: number; height: number } }[] = [], last = 0, frame = 0;

  function size() {
    const ratio = Math.min(devicePixelRatio, 2);
    canvas.width = innerWidth * ratio; canvas.height = innerHeight * ratio; context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }
  size(); addEventListener('resize', size);

  function tick(now: number) {
    // A frame's timestamp can be earlier than the performance.now() taken when the burst began; time must never run backwards.
    const dt = Math.max(0, Math.min(.05, (now - last) / 1000)); last = now;
    context.clearRect(0, 0, innerWidth, innerHeight);
    // If drawing ever throws, the loop must still be able to restart on the next click instead of staying marked as running.
    try {
      bursts = bursts.filter(burst => (burst.particles = stepParticles(context, burst.particles, dt, innerWidth, innerHeight, { clear: false, hide: burst.button })).length > 0);
    } catch (error) { bursts = []; console.error(error); }
    frame = bursts.length ? requestAnimationFrame(tick) : 0;
  }

  /** Sparks and dust slip out from behind the button's edges, favouring the short sides of a wide button, and then fall. */
  function burst(button: DOMRect) {
    // A control removed by a redraw has no edges to emit from.
    if (button.width <= 0 || button.height <= 0) return;
    const cx = button.left + button.width / 2, cy = button.top + button.height / 2, particles: Particle[] = [];
    const outward = () => {
      const x = between(-.9, .9) * button.width / 2, y = between(-.9, .9) * button.height / 2;
      const length = Math.hypot(x / (button.width / 2), y / (button.height / 2)) || 1;
      return { x: cx + x, y: cy + y, dx: x / (button.width / 2) / length, dy: y / (button.height / 2) / length };
    };
    for (let i = 0; i < 24; i++) {
      const start = outward(), speed = between(110, 430);
      particles.push({ kind: 'spark', x: start.x, y: start.y, vx: start.dx * speed + between(-40, 40), vy: start.dy * speed - 80, age: 0, life: between(.25, .75), size: between(1, 2.2) });
    }
    for (let i = 0; i < 6; i++) {
      const start = outward(), speed = between(15, 60);
      particles.push({ kind: 'dust', x: start.x, y: start.y, vx: start.dx * speed, vy: start.dy * speed - 14, age: 0, life: between(.7, 1.3), size: between(14, 34) });
    }
    bursts.push({ particles, button: { left: button.left, top: button.top, width: button.width, height: button.height } });
    if (!frame) { last = performance.now(); frame = requestAnimationFrame(tick); }
  }

  /** A click on a label is followed at once by the click it forwards to its input; one press is one burst. */
  let lastTarget: HTMLElement | null = null, lastTime = 0;
  document.addEventListener('click', event => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(PRESSABLE);
    if (!target) return;
    if (target === lastTarget && event.timeStamp - lastTime < 80) return;
    lastTarget = target; lastTime = event.timeStamp;
    mixer.playSfx(clickUrl, .7);
    if (!reduced()) burst(target.getBoundingClientRect());
  }, true);
}
